import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CyberCard from '../components/CyberCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiSquare, FiHeadphones, FiLock, FiAlertCircle, FiCheck } = FiIcons;

const SOUNDSCAPES = [
  { id: 'none', label: 'Silence' },
  { id: 'rain', label: 'Cyber City Rain' },
  { id: 'noise', label: 'Brown Noise' },
  { id: 'space', label: 'Deep Space' },
];

export default function Cockpit() {
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 mins
  const [isActive, setIsActive] = useState(false);
  const [activeSound, setActiveSound] = useState('noise');
  
  // Distraction Vault State
  const [distractionInput, setDistractionInput] = useState('');
  const [vaultedCount, setVaultedCount] = useState(0);
  const [showVaultSuccess, setShowVaultSuccess] = useState(false);

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      // Play a sound or notification here in a real app
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft]);

  const toggleTimer = () => setIsActive(!isActive);
  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(25 * 60);
  };

  const handleVaultSubmit = (e) => {
    e.preventDefault();
    if (!distractionInput.trim()) return;
    
    // In a real app, save this to a database
    setVaultedCount(prev => prev + 1);
    setDistractionInput('');
    setShowVaultSuccess(true);
    setTimeout(() => setShowVaultSuccess(false), 2000);
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = ((25 * 60 - timeLeft) / (25 * 60)) * 100;

  return (
    <div className="max-w-4xl mx-auto flex flex-col items-center justify-center min-h-[80vh] gap-8">
      
      {/* Main Timer Display */}
      <div className="relative w-72 h-72 md:w-96 md:h-96 flex items-center justify-center">
        {/* Outer glowing ring */}
        <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 100 100">
          <circle 
            cx="50" cy="50" r="48" 
            fill="none" stroke="#1A233A" strokeWidth="2" 
          />
          <motion.circle 
            cx="50" cy="50" r="48" 
            fill="none" stroke="#00F0FF" strokeWidth="2"
            strokeDasharray="301.59" // 2 * pi * 48
            strokeDashoffset={301.59 - (301.59 * progress) / 100}
            strokeLinecap="round"
            className="drop-shadow-[0_0_8px_rgba(0,240,255,0.8)]"
            transition={{ duration: 1, ease: "linear" }}
          />
        </svg>

        <div className="text-center z-10 flex flex-col items-center">
          <div className="text-xs text-[#00F0FF] tracking-[0.3em] uppercase mb-4 font-semibold">
            {isActive ? 'Session Active' : 'System Standby'}
          </div>
          <div className="text-6xl md:text-8xl font-bold font-mono-cyber text-[#F4F1EA] tracking-tighter drop-shadow-md">
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>
          
          <div className="flex gap-4 mt-8">
            <button 
              onClick={toggleTimer}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isActive 
                  ? 'bg-[#1A233A] text-[#F4F1EA] hover:bg-[#2A344A]' 
                  : 'bg-[#00F0FF]/10 border border-[#00F0FF] text-[#00F0FF] laser-glow hover:bg-[#00F0FF]/20'
              }`}
            >
              <SafeIcon icon={isActive ? FiPause : FiPlay} className="text-2xl ml-1" />
            </button>
            <button 
              onClick={resetTimer}
              className="w-14 h-14 rounded-full bg-[#1A233A] text-[#8A95A5] flex items-center justify-center hover:text-[#F4F1EA] transition-colors"
            >
              <SafeIcon icon={FiSquare} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-8">
        {/* Distraction Vault */}
        <CyberCard title="Distraction Vault" action={<SafeIcon icon={FiLock} className="text-[#8A95A5]" />}>
          <div className="relative">
            <p className="text-sm text-[#8A95A5] mb-4">
              Intrusive thought? Log it here and return to focus. We'll save it for later.
            </p>
            <form onSubmit={handleVaultSubmit} className="relative">
              <input
                type="text"
                value={distractionInput}
                onChange={(e) => setDistractionInput(e.target.value)}
                placeholder="e.g., Check email, buy dog food..."
                className="w-full bg-[#050914] border border-[#1A233A] rounded-lg px-4 py-3 text-[#F4F1EA] placeholder-[#4A5568] focus:outline-none focus:border-[#00F0FF]/50 focus:ring-1 focus:ring-[#00F0FF]/50 transition-all font-mono-cyber text-sm"
                disabled={showVaultSuccess}
              />
              <AnimatePresence>
                {showVaultSuccess && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-[#00F0FF] rounded-lg flex items-center justify-center text-[#050914] font-bold gap-2"
                  >
                    <SafeIcon icon={FiCheck} /> Vaulted
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
            <div className="mt-4 flex items-center gap-2 text-xs text-[#8A95A5]">
              <SafeIcon icon={FiAlertCircle} />
              <span>{vaultedCount} thoughts secured this session.</span>
            </div>
          </div>
        </CyberCard>

        {/* Neural Soundscapes */}
        <CyberCard title="Neural Soundscape" action={<SafeIcon icon={FiHeadphones} className="text-[#8A95A5]" />}>
          <div className="grid grid-cols-2 gap-3">
            {SOUNDSCAPES.map((sound) => (
              <button
                key={sound.id}
                onClick={() => setActiveSound(sound.id)}
                className={`px-4 py-3 rounded-lg text-sm transition-all border text-left flex items-center justify-between ${
                  activeSound === sound.id
                    ? 'bg-[#00F0FF]/10 border-[#00F0FF] text-[#00F0FF] laser-glow'
                    : 'bg-[#050914] border-[#1A233A] text-[#8A95A5] hover:border-[#4A5568]'
                }`}
              >
                <span>{sound.label}</span>
                {activeSound === sound.id && (
                  <div className="flex gap-0.5 h-3 items-end">
                    <motion.div animate={{ height: ['40%', '100%', '40%'] }} transition={{ repeat: Infinity, duration: 0.8 }} className="w-1 bg-[#00F0FF] rounded-t-sm" />
                    <motion.div animate={{ height: ['80%', '30%', '80%'] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-1 bg-[#00F0FF] rounded-t-sm" />
                    <motion.div animate={{ height: ['60%', '90%', '60%'] }} transition={{ repeat: Infinity, duration: 0.7 }} className="w-1 bg-[#00F0FF] rounded-t-sm" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </CyberCard>
      </div>
    </div>
  );
}